import { useState, useEffect } from "react";
import { fetchTrackPerformance, extractFundIds } from "../utils/govApi";

function getNormCode(track, sugMutzar) {
  const codeStr = String(track.code || "");
  const sm = String(sugMutzar);
  if (sm === "1" || sm === "5") {
    const trackNum = (codeStr.length >= 7)
      ? parseInt(codeStr.slice(-7), 10)
      : parseInt(track.trackId || codeStr, 10);
    return (!isNaN(trackNum) && trackNum !== 0) ? String(trackNum) : null;
  }
  const cleanId = (codeStr.length >= 7) ? String(parseInt(codeStr.slice(-7), 10)) : (track.trackId || codeStr);
  return (cleanId && cleanId !== "0") ? cleanId : null;
}

function exposureToPct(val, totalAssets) {
  if (val == null || isNaN(val)) return null;
  return val > 100 ? (val / (totalAssets || 1)) * 100 : Number(val);
}

export default function usePortfolioExposure(funds) {
  const [result, setResult] = useState({ loading: true, stock: null, foreign: null, forex: null, liquid: null, matched: 0, total: 0 });

  useEffect(() => {
    if (!funds?.length) {
      setResult({ loading: false, stock: null, foreign: null, forex: null, liquid: null, matched: 0, total: 0 });
      return;
    }

    let cancelled = false;

    async function calc() {
      const allTracks = [];

      for (const fund of funds) {
        // Manual product: use the advisor-entered exposure directly (no gov API).
        // Build a synthetic track weighted by the product's balance so it joins
        // the weighted average alongside real funds.
        if (fund._manual && fund._manualExposure) {
          const e = fund._manualExposure;
          const hasAny = ["stock", "foreign", "forex", "liquid"].some(k => e[k] != null && e[k] !== "");
          if (hasAny) {
            allTracks.push({
              name: fund.tochnit || fund.companyName || "מוצר ידני",
              fundName: fund.companyName || "",
              balance: fund.redeem || 0,
              apiData: {
                STOCK_MARKET_EXPOSURE:    e.stock  != null && e.stock  !== "" ? Number(e.stock)  : null,
                FOREIGN_EXPOSURE:         e.foreign!= null && e.foreign!== "" ? Number(e.foreign): null,
                FOREIGN_CURRENCY_EXPOSURE:e.forex  != null && e.forex  !== "" ? Number(e.forex)  : null,
                LIQUID_ASSETS_PERCENT:    e.liquid != null && e.liquid !== "" ? Number(e.liquid) : null,
                TOTAL_ASSETS: fund.redeem || 0,
              },
              trackKey: fund.id,
            });
          }
          continue;
        }
        if (!fund.maslulim?.length) continue;
        const ids = extractFundIds(fund.maslulim, fund.sugMutzar, fund.companyCode);
        if (!ids.length) continue;

        let perfData = {};
        try {
          perfData = await fetchTrackPerformance(ids, fund.sugMutzar);
        } catch { continue; }

        for (const track of fund.maslulim) {
          const normCode = getNormCode(track, fund.sugMutzar);
          const apiData = normCode && perfData[normCode];
          allTracks.push({
            name: track.name || "—",
            fundName: fund.companyName || "",
            balance: track.balance || 0,
            apiData: apiData || null,
            trackKey: normCode,
          });
        }
      }

      if (cancelled) return;

      const totalBalance = allTracks.reduce((s, t) => s + t.balance, 0);
      const matched = allTracks.filter(t => t.apiData).length;

      if (totalBalance <= 0 || matched === 0) {
        setResult({ loading: false, stock: null, foreign: null, forex: null, liquid: null, matched, total: allTracks.length });
        return;
      }

      const unmatched = allTracks.filter(t => !t.apiData).map(t => ({ name: t.name, fundName: t.fundName, balance: t.balance }));

      let stockW = 0, foreignW = 0, forexW = 0, liquidW = 0;
      let stockSum = 0, foreignSum = 0, forexSum = 0, liquidSum = 0;
      const details = [];

      for (const t of allTracks) {
        if (!t.apiData || t.balance <= 0) continue;
        const w = t.balance / totalBalance;
        const ta = t.apiData.TOTAL_ASSETS;

        const stock = exposureToPct(t.apiData.STOCK_MARKET_EXPOSURE, ta);
        const foreign = exposureToPct(t.apiData.FOREIGN_EXPOSURE, ta);
        const forex = exposureToPct(t.apiData.FOREIGN_CURRENCY_EXPOSURE, ta);
        const liquid = t.apiData.LIQUID_ASSETS_PERCENT != null ? Number(t.apiData.LIQUID_ASSETS_PERCENT) : null;

        if (stock != null) { stockW += w; stockSum += w * stock; }
        if (foreign != null) { foreignW += w; foreignSum += w * foreign; }
        if (forex != null) { forexW += w; forexSum += w * forex; }
        if (liquid != null && !isNaN(liquid)) { liquidW += w; liquidSum += w * liquid; }

        details.push({ name: t.name, fundName: t.fundName, balance: t.balance, weight: w * 100, stock, foreign, forex, liquid, trackKey: t.trackKey });
      }

      const grouped = Object.values(details.reduce((acc, d) => {
        if (d.trackKey && acc[d.trackKey]) {
          acc[d.trackKey].balance += d.balance;
          acc[d.trackKey].weight += d.weight;
        } else {
          const key = d.trackKey || `${d.name}_${d.fundName}_${d.balance}`;
          acc[key] = { ...d };
        }
        return acc;
      }, {}));
      grouped.sort((a, b) => b.balance - a.balance);

      setResult({
        loading: false,
        stock: stockW > 0 ? stockSum / stockW : null,
        foreign: foreignW > 0 ? foreignSum / foreignW : null,
        forex: forexW > 0 ? forexSum / forexW : null,
        liquid: liquidW > 0 ? liquidSum / liquidW : null,
        matched,
        total: allTracks.length,
        details: grouped,
        unmatched,
      });
    }

    setResult(prev => ({ ...prev, loading: true }));
    calc();
    return () => { cancelled = true; };
  }, [funds]);

  return result;
}
